/* ============================================================================
   Typographic split bridge — SplitType
   ----------------------------------------------------------------------------
   Splits copy into lines, words or characters so GSAP can animate them one at
   a time. Used to be done by hand in the markup (see the original hero
   experiment). SplitType lets CMS-authored copy get the same treatment.

   Load from CDN in the page's <head>:

     <script src="https://cdn.jsdelivr.net/npm/split-type@0.3.4/umd/index.min.js"></script>

   Usage:

     import { splitLines } from '../../src/split.js';
     const lines = splitLines('[data-mo-split="lines"]');
     gsap.from(lines, {
       yPercent: 120,
       duration: MOTION.dur.deliberate,
       ease: MOTION.ease.entrance,
       stagger: MOTION.stagger.loose,
     });

   Accessibility: SplitType preserves the original text as an aria-label on
   the wrapper, so screen readers still get the full string. The visual split
   is decorative.

   Reduced motion: skips the split entirely and leaves the copy alone so
   nothing has to un-split under the fallback.
   ========================================================================= */

import { isReduced } from './motion.js';

function ensureLoaded() {
  if (typeof window === 'undefined' || !window.SplitType) {
    console.info('[split] SplitType not found on window — add the CDN <script>.');
    return false;
  }
  return true;
}

/**
 * Split element(s) into lines and wrap each line in an overflow:hidden mask
 * so the classic "line climbs out from underneath" reveal works with no extra
 * markup.
 */
export function splitLines(selectorOrEl) {
  if (isReduced() || !ensureLoaded()) return [];

  const targets = typeof selectorOrEl === 'string' ? document.querySelectorAll(selectorOrEl) : [selectorOrEl];
  const lines = [];

  targets.forEach((el) => {
    if (!el) return;
    // Preserve the source text so screen readers keep the original string
    // even after SplitType has taken the DOM apart.
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.trim());

    const instance = new window.SplitType(el, { types: 'lines', tagName: 'span' });
    instance.lines.forEach((line) => {
      // Wrap each line in a mask so the inner element can translate out from
      // under a clipped edge. Without this the reveal is just a fade.
      const mask = document.createElement('span');
      mask.className = 'mo-mask';
      line.parentNode.insertBefore(mask, line);
      mask.appendChild(line);
      lines.push(line);
    });
  });

  return lines;
}

export function splitWords(selectorOrEl) {
  if (isReduced() || !ensureLoaded()) return [];
  const targets = typeof selectorOrEl === 'string' ? document.querySelectorAll(selectorOrEl) : [selectorOrEl];
  const words = [];
  targets.forEach((el) => {
    if (!el) return;
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.trim());
    const instance = new window.SplitType(el, { types: 'words', tagName: 'span' });
    words.push(...instance.words);
  });
  return words;
}

export function splitChars(selectorOrEl) {
  if (isReduced() || !ensureLoaded()) return [];
  const targets = typeof selectorOrEl === 'string' ? document.querySelectorAll(selectorOrEl) : [selectorOrEl];
  const chars = [];
  targets.forEach((el) => {
    if (!el) return;
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.trim());
    const instance = new window.SplitType(el, { types: 'chars', tagName: 'span' });
    chars.push(...instance.chars);
  });
  return chars;
}
